import React from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'

import { Divider } from '@ui/components/Divider'
import { Modal } from '@ui/components/Modal'

import { PageData } from '@utils/ApiClient'
import {
  TradingAccountRestrictions,
  isRestricted as taIsRestricted,
} from '@utils/trading-account.utils'
import { WalletRestrictions, isRestricted } from '@utils/wallet.utils'

import { TradingAccount } from '@model/TradingAccount'
import { WalletDto } from '@model/WalletDto'

import { Box } from '@shared/components/Box'

import { Balance } from '../Balance'
import { WalletOrTradingAccountType } from '../TransferForm'
import { RadioBoxField, isWalletType } from './TransferTo.modal'

interface Props {
  wallets: WalletDto[]
  tradingAccounts: PageData<TradingAccount> | undefined
  transferFrom: WalletOrTradingAccountType
  onTransferFromChange(transferTo: WalletOrTradingAccountType): void
  transferTo: WalletOrTradingAccountType
}

export const TransferFromModal: BaseModalType<Props> = ({
  wallets,
  tradingAccounts,
  onTransferFromChange,
  transferFrom,
  close,
}) => {
  const { t } = useTranslation()

  return (
    <Modal>
      <Modal.Header>{t('Wallet.Transfer From')}</Modal.Header>
      <Modal.Body>
        {wallets.map((wallet) => {
          const hasRestriction = isRestricted(WalletRestrictions.TRANSFER_FROM, wallet.restrictions)

          const checked = transferFrom?.id === wallet.id

          return [
            <RadioBoxField
              key={`w-${wallet.id}`}
              name='transferFrom'
              tradingAccountOrWallet={wallet}
              onTransferToChange={onTransferFromChange}
              checked={checked}
              isDisabled={hasRestriction}
              caption={
                <Balance
                  balance={wallet.balance}
                  currencyId={wallet.currency.id}
                  isWallet={isWalletType(wallet)}
                  isDisabled={hasRestriction}
                  isTransferFrom
                  onClose={close}
                />
              }
            />,

            tradingAccounts?.items
              .filter((ta) => ta.currency.id === wallet.currency.id)
              .map((ta) => {
                const taRestriction = taIsRestricted(
                  TradingAccountRestrictions.TRANSFER_FROM,
                  ta.restrictions
                )
                const taChecked = transferFrom?.id === ta.id

                return [
                  <Divider.Horizontal key={`div-${ta.id}`} />,
                  <Box key={`box-${ta.id}`} pl='x24'>
                    <RadioBoxField
                      name='transferFrom'
                      tradingAccountOrWallet={ta}
                      checked={taChecked}
                      isDisabled={taRestriction}
                      onTransferToChange={onTransferFromChange}
                      caption={
                        <Balance
                          platformType={ta.platformType}
                          accountType={ta.accountType}
                          balance={ta.platformOverview.balance}
                          currencyId={ta.currency.id}
                          isDisabled={taRestriction}
                          freeMargin={ta.platformOverview.freeMargin}
                          isWallet={isWalletType(ta)}
                          isTransferFrom
                        />
                      }
                    />
                  </Box>,
                ]
              }),
          ]
        })}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal>
  )
}
